/**
 * Recovery for lazy route chunks whose network request failed.
 *
 * A module whose fetch fails is recorded as a failure in the document's module map, so importing
 * the same URL again rejects instantly without another request: the route stays dead for the rest
 * of the page session even after the network recovers. Only a different URL is a different
 * module-map key, which is why a retry has to append a query string instead of calling the loader
 * again. Vite's `__vitePreload` has the same problem for stylesheets — it marks every dep `seen`
 * before the `<link>` resolves and never retries one — so a failed stylesheet has to be re-added
 * by hand.
 */

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 400;
const ASSETS_PATH_PREFIX = '/assets/';
const RETRY_QUERY_PARAM = 'lazy-retry';

// How each engine words a dynamic import whose *fetch* failed, plus Vite's own stylesheet message.
// A module that threw while evaluating rejects with that error instead, and must never be retried:
// re-running it would only reproduce the exception.
const FETCH_FAILURE_PATTERNS = [
  /failed to fetch dynamically imported module/iu,
  /error loading dynamically imported module/iu,
  /importing a module script failed/iu,
  /unable to preload css for/iu
];

// Chromium and Firefox name an absolute URL, Vite names a root-relative stylesheet path.
const ASSET_URL_PATTERN = /(?:https?:\/\/[^\s'"]+)?\/assets\/[^\s'")]+/u;

const LOADER_IMPORT_PATTERN = /\bimport\s*\(\s*(?<quote>['"])(?<path>[^'"]+)\k<quote>\s*\)/u;

let retrySequence = 0;

/**
 * @param {unknown} error
 * @returns {boolean} true when the module could not be fetched, as opposed to failing to evaluate.
 */
export function isLazyImportFailure(error) {
  const message = error?.message;
  if (typeof message !== 'string') {
    return false;
  }
  return FETCH_FAILURE_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * A chunk request cut short because the client went offline, or because the document is hidden and
 * on its way out, is not an application fault. That covers most of the volume on this error: email
 * link scanners that abandon the page mid-boot, and people who navigate away, close the tab, or
 * sleep the machine while a route is still loading.
 * @returns {boolean}
 */
export function isClientAbandoningPage() {
  if (navigator.onLine === false) {
    return true;
  }
  return document.visibilityState === 'hidden';
}

/**
 * @param {unknown} error
 * @returns {boolean} true when the failure is a chunk fetch the client itself walked away from.
 */
export function isUnreportableChunkFailure(error) {
  return isLazyImportFailure(error) && isClientAbandoningPage();
}

/**
 * The URL is read out of an error message, so resolve it and confirm it addresses our own build
 * output before handing it back to `import()` or a `<link>`.
 * @param {unknown} error
 * @returns {URL|null}
 */
export function extractAssetUrl(error) {
  const match = ASSET_URL_PATTERN.exec(error?.message ?? '');
  if (!match) {
    return null;
  }

  let url;
  try {
    url = new URL(match[0], window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin || !url.pathname.startsWith(ASSETS_PATH_PREFIX)) {
    return null;
  }
  return url;
}

/** Vite leaves the built chunk path as a literal import in each lazy loader. */
function extractLoaderAssetUrl(loader) {
  const chunkPath = LOADER_IMPORT_PATTERN.exec(loader.toString())?.groups?.path;
  if (!chunkPath) {
    return null;
  }

  try {
    const url = new URL(chunkPath, import.meta.url);
    return url.origin === window.location.origin &&
      url.pathname.startsWith(ASSETS_PATH_PREFIX) &&
      url.pathname.endsWith('.js')
      ? url
      : null;
  } catch {
    return null;
  }
}

function cacheBust(url) {
  retrySequence += 1;
  const retryUrl = new URL(url);
  retryUrl.searchParams.set(RETRY_QUERY_PARAM, String(retrySequence));
  return retryUrl.href;
}

function delayFor(milliseconds) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}

function importByUrl(url) {
  return import(/* @vite-ignore */ url);
}

function resolveLinkUrl(link) {
  const href = link.getAttribute('href');
  if (!href) {
    return null;
  }
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith(ASSETS_PATH_PREFIX)
      ? url
      : null;
  } catch {
    return null;
  }
}

/**
 * Add a cache-busted stylesheet after `staleLink` when there is one so it keeps its position
 * in the cascade without removing a link that still has pending load/error listeners. Resolves
 * either way: missing styles degrade the page, they do not break the route.
 */
function loadStylesheet(url, staleLink) {
  return new Promise(resolve => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.crossOrigin = '';
    link.href = cacheBust(url);
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () => resolve());

    if (staleLink) {
      staleLink.after(link);
    } else {
      document.head.appendChild(link);
    }
  });
}

/**
 * Restore every build stylesheet the document is missing, not just the one the error named: Vite
 * reports only the first rejected dep of a preload call and discards the rest, while marking all of
 * them `seen` so it will never retry any of them. A `<link>` whose request failed has no
 * `CSSStyleSheet` attached, which is how the discarded ones are found. One that is merely still in
 * flight looks the same, but adding a link after it only costs a repeated request.
 *
 * @param {URL} reportedUrl the stylesheet named by the error, restored even if it looks loaded
 * @returns {Promise<void>}
 */
export function reinjectFailedStylesheets(reportedUrl) {
  const candidates = [...document.querySelectorAll('link[rel="stylesheet"]')]
    .map(link => ({ link, url: resolveLinkUrl(link) }))
    .filter(({ url }) => url !== null);

  const broken = candidates.filter(
    ({ link, url }) => link.sheet === null || url.pathname === reportedUrl.pathname
  );

  if (broken.length === 0) {
    // Vite appends the `<link>` before awaiting it, so reaching here means something else removed
    // it in the meantime.
    return loadStylesheet(reportedUrl, null);
  }

  return Promise.all(broken.map(({ link, url }) => loadStylesheet(url, link))).then(
    () => undefined
  );
}

/**
 * Wrap a route's lazy component loader so a dropped chunk request is retried instead of killing
 * the route for the rest of the session.
 *
 * @param {() => Promise<unknown>} loader the original `() => import('...')`
 * @param {{
 *   maxAttempts?: number,
 *   delay?: (milliseconds: number) => Promise<void>,
 *   importModule?: (url: string) => Promise<unknown>,
 *   restoreStylesheets?: (reportedUrl: URL) => Promise<void>
 * }} [options]
 * @returns {() => Promise<unknown>}
 */
export function retryLazyImport(loader, options = {}) {
  const {
    maxAttempts = MAX_ATTEMPTS,
    delay = delayFor,
    importModule = importByUrl,
    restoreStylesheets = reinjectFailedStylesheets
  } = options;

  // Recursive rather than a loop: an attempt cannot begin until the previous one has failed and its
  // backoff has elapsed, so every await here is deliberately sequential — which is the shape
  // `no-await-in-loop` exists to reject. Folding this back into a `for` loop fails `npm run lint`.
  async function attemptLoad(attempt, previousFailure) {
    try {
      return attempt === 0
        ? await loader()
        : await retryFailedLoad(previousFailure, { loader, importModule, restoreStylesheets });
    } catch (error) {
      if (!isLazyImportFailure(error)) {
        throw error;
      }

      if (attempt + 1 >= maxAttempts) {
        // Read by the Sentry beforeSend hook to tell exhausted retries from a single dropped request.
        error.lazyImportAttempts = attempt + 1;
        throw error;
      }

      await delay(RETRY_DELAY_MS * (attempt + 1));
      return attemptLoad(attempt + 1, error);
    }
  }

  return function loadLazyRoute() {
    return attemptLoad(0);
  };
}

async function retryFailedLoad(failure, { loader, importModule, restoreStylesheets }) {
  const url = extractAssetUrl(failure) ?? extractLoaderAssetUrl(loader);
  if (!url) {
    // No safe chunk URL is available (for example, in development before Vite emits assets).
    return loader();
  }

  if (url.pathname.endsWith('.css')) {
    // Vite already marked this dep `seen`, so the loader will skip it from here on: restore the
    // stylesheets ourselves, then let the loader fetch the module as usual.
    await restoreStylesheets(url);
    return loader();
  }

  return importModule(cacheBust(url));
}
